import React, { useState } from 'react';
import { signIn as authSignIn } from 'aws-amplify/auth';
import { Input } from 'reactstrap';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { authActions } from 'redux/auth';
import { store } from 'redux/store';
import StyledReactButton from 'components/Buttons/StyledButton';
import { idPattern } from 'constants/validators';
import { useToast } from 'hooks/useToast';
import ErrorMessage from './ErrorMessage';
import { AuthError } from '../models';
import VieurekaLogo from 'assets/images/vieureka_vertical@2x.png';

const SignIn: React.FC = () => {
  const { t } = useTranslation(['signin', 'formError']);
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    reValidateMode: 'onSubmit',
  });
  const { clearToasts } = useToast();

  const [errorInfo, setErrorInfo] = useState<{
    code?: string;
    message?: string;
  }>({});
  const [signInParams, setSignInParams] = useState({
    id: '',
    password: '',
  });

  const signin = (id: string, password: string): void => {
    setErrorInfo({});
    authSignIn({ username: id, password })
      .then(({ isSignedIn, nextStep }) => {
        const { signInStep } = nextStep;
        clearToasts();
        if (signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
          store.dispatch(authActions.newPassword(true));
        } else {
          if (isSignedIn) {
            store.dispatch(authActions.login());
          } else {
            throw new Error('Signin failed');
          }
        }
      })
      .catch(error => {
        const errorCode = error instanceof Error ? error.name : '';
        let errorMessage = error instanceof Error ? error.message : '';
        switch (errorCode) {
          case AuthError.userNotConfirmedException:
            errorMessage = t('userNotConfirmedException');
            break;
          case AuthError.passwordResetRequiredException:
            errorMessage = t('passwordResetRequiredException');
            break;
          case AuthError.notAuthorizedException:
            errorMessage = t('notAuthorizedException');
            break;
          case AuthError.userNotFoundException:
            errorMessage = t('userNotFoundException');
            break;
        }
        setErrorInfo({
          code: errorCode,
          message: errorMessage,
        });
      });
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name: key, value } = e.target;
    setValue(key, value);
    setSignInParams(prevState => ({ ...prevState, [key]: value }));
  };

  const onClickForgotPassword = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ): void => {
    e.preventDefault();
    store.dispatch(authActions.forgetPassword(true));
  };

  const { message: errorMessage } = errorInfo;
  const { id, password } = signInParams;
  return (
    <Container>
      <StyledForm onSubmit={handleSubmit(() => signin(id, password))}>
        <StyledImage src={VieurekaLogo} />
        <StyledTitle>SDカード録画・閲覧システム</StyledTitle>
        <StyledInput
          {...register('id', {
            required: true,
            pattern: idPattern,
          })}
          value={id}
          onChange={onChange}
          placeholder={t('signin:id')}
        />
        <StyledInput
          {...register('password', {
            required: true,
          })}
          type={'password'}
          value={password}
          onChange={onChange}
          placeholder={t('signin:password')}
        />
        <StyledSubmitButton>
          <StyledReactButton color="primary" block>
            {t('signin:login')}
          </StyledReactButton>
        </StyledSubmitButton>
        {(!_.isEmpty(errors) || errorMessage !== null) && (
          <ErrorMessage errors={errors} errorMessage={errorMessage} />
        )}
        <div>
          <StyledAnchor href={'#'} onClick={onClickForgotPassword}>
            {t('signin:forgotPassword')}
          </StyledAnchor>
        </div>
      </StyledForm>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100vw;
  height: 75vh;
  text-align: center;
`;

const StyledForm = styled.form`
  margin-top: 100px;
`;

const StyledTitle = styled.p`
  margin-top: 40px;
  margin-bottom: 15px;
  font-size: 1.5rem;
`;

const StyledInput = styled(Input)`
  width: 280px;
  margin: 0 auto 15px auto;
  font-size: 0.8rem;
`;

const StyledSubmitButton = styled.div`
  width: 280px;
  margin: 0 auto 20px auto;
`;

const StyledAnchor = styled.a`
  :hover {
    text-decoration: underline;
  }
`;

const StyledImage = styled.img`
  width: 150px;
`;

export default SignIn;
